/**
 * Sheet (web) — Bottom sheet with drag-to-dismiss via pointer events.
 *
 * Portal-based like Dialog.web.tsx. Drag anywhere on the sheet to dismiss.
 * Dismisses on: 30% drag OR velocity > 0.5px/ms.
 */
import { useRef, useState, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { styled, YStack, Theme, useThemeName } from 'tamagui'

const SheetCard = styled(YStack, {
  name: 'SheetCard',
  bg: '$backgroundModal',
  btlr: '$xl',
  btrr: '$xl',
  overflow: 'hidden',
  w: '100%',
})

const Handle = styled(YStack, {
  name: 'SheetHandle',
  h: 4,
  br: 2,
  bg: '$borderColor',
  mt: '$sm',
  mb: '$xs',
  cursor: 'grab',
})

interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  noHandle?: boolean
}

const DISMISS_THRESHOLD = 0.3
const VELOCITY_THRESHOLD = 0.5 // px/ms

export function Sheet({ open, onOpenChange, children, noHandle }: SheetProps) {
  const themeName = useThemeName()
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startY = useRef(0)
  const lastY = useRef(0)
  const lastTime = useRef(0)
  const velocity = useRef(0)
  const sheetRef = useRef<HTMLDivElement>(null)

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    startY.current = e.clientY
    lastY.current = e.clientY
    lastTime.current = Date.now()
    velocity.current = 0
    setIsDragging(true)
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return
    const now = Date.now()
    const dt = now - lastTime.current
    if (dt > 0) {
      velocity.current = (e.clientY - lastY.current) / dt
    }
    lastY.current = e.clientY
    lastTime.current = now
    const dy = e.clientY - startY.current
    if (dy > 0) setDragY(dy)
  }, [isDragging])

  const onPointerUp = useCallback(() => {
    if (!isDragging) return
    setIsDragging(false)
    const sheetHeight = sheetRef.current?.offsetHeight || 300
    const shouldDismiss =
      dragY > sheetHeight * DISMISS_THRESHOLD ||
      velocity.current > VELOCITY_THRESHOLD
    if (shouldDismiss) {
      onOpenChange(false)
    }
    setDragY(0)
  }, [isDragging, dragY, onOpenChange])

  return createPortal(
    <Theme name={themeName} forceClassName>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          backgroundColor: 'rgba(62, 66, 58, 0.4)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.2s ease',
        }}
        onClick={() => onOpenChange(false)}
      />
      {/* Sheet — pinned to bottom center */}
      <div
        ref={sheetRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transform: open
            ? `translateY(${dragY}px)`
            : 'translateY(100%)',
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          pointerEvents: open ? 'auto' : 'none',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <SheetCard maxWidth={600}>
          {!noHandle && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8, paddingBottom: 4 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: '#C8C8C2', cursor: 'grab' }} />
            </div>
          )}
          {children}
        </SheetCard>
      </div>
    </Theme>,
    document.body,
  )
}
